import React from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Icon,
  Typography,
} from "@mui/material";
import { PieChartOutlined } from "@ant-design/icons";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import MainCard from "components/MainCard";
import { IMemberTypeDistributionItem } from "../api/types";

interface MemberTypePieChartProps {
  data?: IMemberTypeDistributionItem[];
  isLoading: boolean;
}

const COLORS = [
  "#1890ff",
  "#52c41a",
  "#faad14",
  "#f5222d",
  "#722ed1",
  "#13c2c2",
  "#fa8c16",
];

export default function MemberTypePieChart({
  data,
  isLoading,
}: MemberTypePieChartProps) {
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map((d) => ({
      name: d.memberTypeName || "Other",
      value: d.visitCount,
    }));
  }, [data]);

  const hasData = chartData.length > 0;

  return (
    <MainCard sx={{ mt: 1, height: "100%" }}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h4" display="flex" alignItems="center">
            <Icon sx={{ mr: 1 ,pb : 4,color : "primary.main"}}>
              <PieChartOutlined />
            </Icon>
            Visits by Member Type
          </Typography>
        </Grid>
      </Grid>

      <Divider sx={{my : 1.67 }} />

      <Box sx={{ height: { xs: 280, sm: 340, md: 380 }, width: "100%" }}>
        {isLoading ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : !hasData ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">
              No member data for this period.
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="45%"
                innerRadius={50}
                outerRadius={85}
                paddingAngle={3}
                dataKey="value"
                label={({ name, percent }) =>
                  `${name} (${(percent * 100).toFixed(0)}%)`
                }
                labelLine={false}
              >
                {chartData.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number | string | undefined) => [
                  `${value} Visits`,
                  "Visits",
                ]}
              />
              <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Box>
    </MainCard>
  );
}

